import { orderBy } from 'lodash-es';
import { getPerfusionTime, isClinicalRecordOffOcs } from '@features/components/ClinicalData/utils';
import { BackgroundStyleKeys } from '@features/components/ClinicalData/components/ClinicalLog/components/constants';
import { ClinicalItem, ClinicalItemLog, WithPerfusionTime } from '@features/components/ClinicalData/types';
import { getClinicalItemTimestamp, mapMediaFilesWithTimestamp } from '@features/components/ClinicalData/sections/utils';
import {
  clinicalLogEvents,
  lungsTerminationClinicalLogs,
} from '@features/components/ClinicalData/components/ClinicalLog/constants';
import { clinicalDataGroupedItems } from '@features/components/ClinicalData';
import { getClinicalRecordByType } from '@/utils/helpers';
import {
  ClinicalData,
  BaseClinicalDataItem,
  ClinicalDataRecord,
  ClinicalItemType,
  ClinicalMediaFile,
  PerfusionTerminationType,
  TimestampWithZone,
  DonorSpecimenHandoffRecord,
  MultipleDonorSpecimenHandoffRecord,
  ServiceConfirmationRecord,
  PerfusionTerminationRecord,
  DonorSpecimenHandoff,
} from '@/api/apis/case';

export const getBaseEventVariant = (type: ClinicalItemType) => {
  if ([ClinicalItemType.RecordDelay, ClinicalItemType.DonorDidNotExpireInTime, ClinicalItemType.DryRun].includes(type))
    return 'danger';
  if (type === ClinicalItemType.CardiacArrest) return 'success';
  return 'base';
};

export const getStatusEventVariant = (type: ClinicalItemType): BackgroundStyleKeys => {
  if (
    [
      ClinicalItemType.OrganOnOCS,
      ClinicalItemType.OrganOnSCS,
      ClinicalItemType.OffOCS,
      ClinicalItemType.OffSCS,
      ClinicalItemType.OnOCSVideo,
      ClinicalItemType.PerfusionTerminationOption,
      ClinicalItemType.ScsTerminationOption,
      ClinicalItemType.RecordDelay,
    ].includes(type)
  )
    return BackgroundStyleKeys.maroon;
  return BackgroundStyleKeys.base;
};

const getDonorSpecimentFiles = (record?: DonorSpecimenHandoff & BaseClinicalDataItem): ClinicalMediaFile[] => {
  if (!record) return [];
  const { mediaFiles } = record;
  const files: ClinicalMediaFile[] = [];
  if (record?.signature) {
    const signatureFile: ClinicalMediaFile = { ...record.signature?.file, mediaType: 'Photo' };
    files.push({ ...signatureFile, preview: signatureFile });
  }
  if (mediaFiles) files.push(...mediaFiles);

  return files;
};

export const getDonorSpecimentHandoffLog = (record: DonorSpecimenHandoffRecord) => {
  return {
    ...record,
    item: {
      ...record.item,
      mediaFiles: getDonorSpecimentFiles(record.item),
    },
  };
};

export const getServiceConfirmationLog = (record: ServiceConfirmationRecord) => {
  const { retrievalSurgery, ocsManagement, retrievalSurgeon, ocsSpecialist, txRepresentative } = record.item;
  const dateObjects: { timestamp: string; timeZone: string }[] = [];
  if (retrievalSurgery)
    dateObjects.push({ timestamp: retrievalSurgery.surgeryDate, timeZone: retrievalSurgery.surgeryDateTimeZone });
  if (ocsManagement)
    dateObjects.push({ timestamp: ocsManagement.serviceDate, timeZone: ocsManagement.serviceDateTimeZone });
  if (retrievalSurgeon)
    dateObjects.push({ timestamp: retrievalSurgeon.timestamp, timeZone: retrievalSurgeon.timeZone });
  if (ocsSpecialist) dateObjects.push({ timestamp: ocsSpecialist.timestamp, timeZone: ocsSpecialist.timeZone });
  if (txRepresentative)
    dateObjects.push({ timestamp: txRepresentative.timestamp, timeZone: txRepresentative.timeZone });

  const latest = orderBy(dateObjects, 'timestamp', 'desc')[0];
  return {
    clinicalItemType: record.clinicalItemType,
    item: {
      ...record.item,
      timestamp: latest?.timestamp,
      timeZone: latest?.timeZone,
    },
  };
};

export const getPerfusionTerminationLogs = (record: PerfusionTerminationRecord): ClinicalDataRecord[] => {
  const { sequentialLungFlush, doubleLungFlush } = record.item;
  const records: ClinicalDataRecord[] = [];
  const getItemData = (timeData: TimestampWithZone, type: PerfusionTerminationType) => {
    return {
      clinicalItemType: record.clinicalItemType,
      item: {
        id: `${record.item.id}-${type}`,
        ...timeData,
        details: type.toString(),
      },
    };
  };
  if (sequentialLungFlush?.firstLungFlushTime) {
    const { firstLungFlushTime } = sequentialLungFlush;
    const firstLungItem = getItemData(firstLungFlushTime, PerfusionTerminationType.firstLungFlush);
    records.push(firstLungItem);
  }
  if (sequentialLungFlush?.secondLungFlushTime) {
    const { secondLungFlushTime } = sequentialLungFlush;
    const secondLungItem = getItemData(secondLungFlushTime, PerfusionTerminationType.secondLungFlush);
    records.push(secondLungItem);
  }
  if (doubleLungFlush?.flushTime) {
    const doubleLungItem = getItemData(doubleLungFlush?.flushTime, PerfusionTerminationType.doubleLungFlush);
    records.push(doubleLungItem);
  }
  return records;
};

export const mapWithPerfusionTime = <T extends Omit<ClinicalItem, 'mediaFiles'>>(
  logRecords: T[],
  ocsStartTime: TimestampWithZone | null,
): WithPerfusionTime<T>[] => {
  const orderedRecords = orderBy(logRecords, 'timestamp', 'asc');
  let ptShouldBeUpdatedOnNextIteration: boolean = false;
  let perfusionTimeShown: boolean = false;

  return orderedRecords.map((logRecord: T, index: number) => {
    if (ptShouldBeUpdatedOnNextIteration) {
      ptShouldBeUpdatedOnNextIteration = false;
      perfusionTimeShown = !perfusionTimeShown;
    }

    if (logRecord.clinicalItemType === ClinicalItemType.OrganOnOCS) {
      perfusionTimeShown = true;
    }

    if (isClinicalRecordOffOcs(logRecord, orderedRecords.slice(0, index))) {
      ptShouldBeUpdatedOnNextIteration = true;
    }

    return {
      ...logRecord,
      perfusionTime:
        ocsStartTime && perfusionTimeShown ? getPerfusionTime(logRecord.timestamp, ocsStartTime.timestamp) : null,
    };
  });
};

export const getMultipleDonorSpecimentHandoffLogs = (
  record: MultipleDonorSpecimenHandoffRecord,
): DonorSpecimenHandoffRecord[] => {
  if (!record.items || record.items.length === 0) return [];

  return record.items.map((item) => ({
    clinicalItemType: ClinicalItemType.DonorSpecimenHandoff,
    item: {
      ...item,
      mediaFiles: getDonorSpecimentFiles(item),
    },
  }));
};

const mapLogs = (clinicalRecords: ClinicalDataRecord[]): WithPerfusionTime<ClinicalItemLog>[] => {
  const ocsStartClinicalRecord = getClinicalRecordByType(clinicalRecords, ClinicalItemType.OrganOnOCS);
  const ocsStartTime = getClinicalItemTimestamp(ocsStartClinicalRecord);
  const logRecords: ClinicalItemLog[] = clinicalRecords
    .map((record: ClinicalDataRecord) => {
      const { item, items, clinicalItemType } = record;
      if (item) {
        return {
          ...item,
          mediaFiles: mapMediaFilesWithTimestamp(item),
          clinicalItemType,
        };
      }
      if (items) {
        const mappedFiles = items.flatMap((c) => {
          return mapMediaFilesWithTimestamp(c);
        });
        const itemsOrdered = orderBy(items, 'timestamp');
        // TODO vitals perfusion time is incorrect, we have to take the earliest item from sort and not the first item,
        const firstRecord = itemsOrdered[0];

        return {
          ...firstRecord,
          clinicalItemType,
          mediaFiles: mappedFiles,
        };
      }
      return null;
    })
    .filter((i) => i !== null) as ClinicalItemLog[];

  return mapWithPerfusionTime<ClinicalItemLog>(logRecords, ocsStartTime).reverse();
};

export const filterLogs = (clinicalData: ClinicalData, isTrialCase: boolean) => {
  return clinicalData.filter((l) => {
    const isRegularClinicalLogEvent = clinicalLogEvents.includes(l.clinicalItemType);
    const isRandomizationEvent = l.clinicalItemType === ClinicalItemType.Randomization;
    const isRandomizationDisplayed = isRandomizationEvent && isTrialCase;

    return isRegularClinicalLogEvent || isRandomizationDisplayed;
  });
};

export const getLogItems = (clinicalData: ClinicalData, isTrialCase: boolean): WithPerfusionTime<ClinicalItemLog>[] => {
  const records: ClinicalDataRecord[] = [];
  const logs: ClinicalDataRecord[] = filterLogs(clinicalData, isTrialCase);
  logs.forEach((event: ClinicalDataRecord) => {
    if (event.clinicalItemType === ClinicalItemType.MultipleDonorSpecimenHandoff) {
      records.push(...getMultipleDonorSpecimentHandoffLogs(event));
      return;
    }
    if (event.items?.length && !clinicalDataGroupedItems[event.clinicalItemType]) {
      const mappedEvents =
        event.items?.map((item, index) => ({ clinicalItemType: event.clinicalItemType, item, index })) || [];
      records.push(...(mappedEvents as unknown as ClinicalDataRecord[]));
    } else if (lungsTerminationClinicalLogs.includes(event.clinicalItemType)) {
      records.push(...getPerfusionTerminationLogs(event as PerfusionTerminationRecord));
    } else if (event.clinicalItemType === ClinicalItemType.ServiceConfirmation) {
      records.push(getServiceConfirmationLog(event));
    } else if (event.clinicalItemType === ClinicalItemType.DonorSpecimenHandoff) {
      records.push(getDonorSpecimentHandoffLog(event));
    } else {
      records.push(event);
    }
  });
  return mapLogs(records);
};
